import { AwsRum, type AwsRumConfig } from 'aws-rum-web';
import { config } from '../config';

/**
 * CloudWatch RUM web-client wrapper (S17a / ADR-0027).
 *
 * The deployed branch of {@link reportError} forwards caught errors via
 * {@link rumRecordError} and the targeted create-flow custom event via
 * {@link rumRecordEvent}. The uncaught path (`window.onerror` /
 * `unhandledrejection`) is owned by RUM's built-in `errors` telemetry — we
 * deliberately do not install global window handlers here (that was the S13
 * `installGlobalErrorHandlers` source, removed at S17a to avoid double-
 * reporting).
 *
 * Identity enrichment (the accepted-PII decision, ADR-0027): the
 * authenticated tenant + username (Auth0 email) flow in through
 * {@link setRumIdentity}, which both stamps them onto the SHARED session
 * (so page-view + performance events are attributable) AND seeds the
 * module-level cache that {@link rumRecordError}/{@link rumRecordEvent}
 * stamp onto every event payload. Credentials are NEVER attached — see
 * the SR1/SR6 scrub in `reportError.ts`.
 *
 * Env-gated: when `config.rum` is undefined (any local-dev run, or a
 * deployed build the CI inject step skipped) every export here is a
 * no-op — the SPA boots and runs without RUM, the caught-error path
 * falls through to the console-log dev branch in `reportError`.
 *
 * StrictMode-safe: `initRum()` is idempotent; double-mount (StrictMode
 * remount during dev) reuses the existing instance.
 */

let awsRum: AwsRum | undefined;
let initAttempted = false;
let currentIdentity: RumIdentity | undefined;

export interface RumIdentity {
  /** Tenant slug — the `https://agentisai/tenantSlug` Auth0 namespace claim. */
  readonly tenant: string;
  /** Auth0 email (per ADR-0027 — sub is the documented alternative). */
  readonly username: string;
}

/**
 * Initialise the RUM web client. Idempotent.
 *
 * Called once from `main.tsx` BEFORE app render. In deployed envs with a
 * full `config.rum` it spins up the `AwsRum` instance; otherwise it
 * records the attempt and exits — every other export becomes a no-op
 * cheap-fail.
 */
export function initRum(): void {
  if (initAttempted) return;
  initAttempted = true;

  // Only initialise in a deployed build with a full RUM config. A dev
  // build, or a deployed build the CI inject step skipped, takes the
  // no-op path: the caught-error sink stays the console branch.
  if (!config.isDeployedEnv) return;
  if (!config.rum) return;

  const cfg: AwsRumConfig = {
    sessionSampleRate: 1.0,
    identityPoolId: config.rum.identityPoolId,
    guestRoleArn: config.rum.guestRoleArn,
    // RUM data-plane endpoint for this region. The client mirrors the
    // server-side construct's telemetry set (errors/performance/http) and adds
    // enableXRay (stamp trace IDs) + allowCookies (dedupe sessions across page
    // loads) below.
    endpoint: `https://dataplane.rum.${config.rum.region}.amazonaws.com`,
    // The `http` telemetry carries `addXRayTraceIdHeader` so the browser stamps
    // the X-Ray `X-Amzn-Trace-Id` header on requests to the API origin ONLY
    // (scoped by the `config.apiBaseUrl` prefix). That propagated header is what
    // lets the backend Lambda adopt this RUM session's trace id and join one
    // end-to-end X-Ray trace (client -> API -> Lambda; S17c / ADR-0028). Scoping
    // to the API origin keeps it off Auth0 + same-origin requests so no needless
    // CORS preflight is triggered elsewhere. (`enableXRay: true` below is the
    // prerequisite for the header.)
    telemetries: [
      'errors',
      'performance',
      ['http', { addXRayTraceIdHeader: [new RegExp(`^${escapeRegExp(config.apiBaseUrl)}`)] }],
    ],
    allowCookies: true,
    enableXRay: true,
    // Defence-in-depth on the Auth0 OAuth callback (the construct's
    // `excludedPages` ALSO blocks it on the server side). Even if a
    // browser somehow lands on /callback with RUM live, the page URL
    // (carrying `?code/state`) is not captured.
    disableAutoPageView: false,
  };

  try {
    awsRum = new AwsRum(config.rum.appMonitorId, '1.0.0', config.rum.region, cfg);
  } catch {
    // RUM init failures (network, IAM, invalid id) MUST NOT crash the app
    // — the SPA continues without telemetry, the caught-error sink falls
    // through to the no-op branch.
    awsRum = undefined;
  }
}

/**
 * Stamp the authenticated tenant + username onto the current RUM session
 * (so page-view + performance events are attributable), and cache them
 * for {@link rumRecordError}/{@link rumRecordEvent} to inline on every
 * subsequent event payload. Call from the Auth0 provider's effect once
 * the user becomes authenticated; safe to call repeatedly.
 */
export function setRumIdentity(identity: RumIdentity): void {
  currentIdentity = identity;
  awsRum?.addSessionAttributes({
    tenant: identity.tenant,
    username: identity.username,
  });
}

/** Drop the identity (e.g. after logout). The session attribute survives until the next page load. */
export function clearRumIdentity(): void {
  currentIdentity = undefined;
}

/** Read the cached identity (used by reportError to inline tenant+username on every error event). */
export function getRumIdentity(): RumIdentity | undefined {
  return currentIdentity;
}

/**
 * Forward a caught error to RUM. No-op when RUM is not initialised.
 *
 * The `details` object goes onto the RUM event's payload — we deliberately
 * pass `tenant + username` here too (in addition to the session-attribute
 * stamp) so a SINGLE event JSON carries both identity + error context,
 * which matters when the event is inspected via Logs Insights (where
 * session attributes are a side channel).
 */
export function rumRecordError(error: unknown, details?: Record<string, unknown>): void {
  if (!awsRum) return;
  const identity = currentIdentity;
  const payload: Record<string, unknown> = {
    ...(details ?? {}),
    ...(identity ? { tenant: identity.tenant, username: identity.username } : {}),
  };
  awsRum.recordError(buildErrorForRum(error, payload));
}

/**
 * Emit the targeted `com.agentisai.app_error` custom event the
 * RumAppMonitor's metric definition pattern-matches against (drives the
 * `CreateFlowErrorCount` custom metric / alarm).
 *
 * `route + errorName + tenant` become metric dimensions; `username` rides
 * as an event attribute only (NOT a dimension — explodes cardinality).
 */
export function rumRecordEvent(
  eventType: 'com.agentisai.app_error',
  details: { route: string; source: string; errorName: string },
): void {
  if (!awsRum) return;
  const identity = currentIdentity;
  awsRum.recordEvent(eventType, {
    ...details,
    ...(identity ? { tenant: identity.tenant, username: identity.username } : {}),
  });
}

/** Hook for tests — reset module state between cases. NOT for production use. */
export function __resetRumForTesting(): void {
  awsRum = undefined;
  initAttempted = false;
  currentIdentity = undefined;
}

/**
 * RUM's `recordError` accepts either an `Error` or a plain object. We
 * carry the structured payload (already scrubbed by the caller) on the
 * Error's `cause` so the RUM dashboard's error grouping (by message +
 * stack) stays clean — without forcing a `details` field onto the
 * stringified message.
 */
function buildErrorForRum(error: unknown, payload: Record<string, unknown>): Error {
  if (error instanceof Error) {
    // Attach payload as `cause` for structured inspection (RUM serialises
    // `cause` alongside the stack).
    if (Object.keys(payload).length > 0) {
      return new Error(error.message, { cause: { source: payload, original: error.cause } });
    }
    return error;
  }
  return new Error(String(error), { cause: payload });
}

/**
 * Escape regex metacharacters so a config URL (e.g. `config.apiBaseUrl`) can be
 * used as a literal prefix in the `addXRayTraceIdHeader` allow-pattern — the
 * `.`/`/` in the URL must not be treated as regex syntax.
 */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
